import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Typography, TextField, Button, MenuItem,
  FormControl, FormControlLabel, RadioGroup, Radio, Checkbox, FormGroup,
  Autocomplete, Alert, CircularProgress, ToggleButtonGroup, ToggleButton,
  Snackbar, FormHelperText, Stack, Chip,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { publicScreeningAPI } from '../services/api';

type Lang = 'en' | 'fr';

const STR: Record<Lang, Record<string, string>> = {
  en: {
    title: 'Traveller Health Screening — Points of Entry',
    subtitle: 'Ebola / Mpox entry form — no login required. Temperature taken at checkpoint.',
    eyebrow: 'DRC · Ministry of Public Health',
    step1Title: 'Phone number & document scan',
    phone: 'Phone number',
    dialCode: 'Country code',
    step2Title: 'Scan your passport / ID (required)',
    scanHint: 'Take a photo or upload your passport — fields will be auto-filled below.',
    takePhoto: 'Take photo',
    upload: 'Upload file',
    ocrRunning: 'Reading document…',
    ocrDone: 'Document data pre-filled — you can still edit any field.',
    uploadRequiredNotice: 'Enter your phone number and scan or upload your passport / ID to unlock the form below.',
    unlockHint: 'Complete phone number and document upload above to continue.',
    identity: 'Traveler Identity',
    fullName: 'Full name', sex: 'Gender', male: 'Male', female: 'Female',
    dob: 'Date of birth', age: 'Age (years)', ageHint: 'Calculated automatically from date of birth.',
    nationality: 'Nationality', idType: 'ID type', passport: 'Passport', nationalId: 'National ID', other: 'Other',
    idNumber: 'Passport / ID number', residence: 'Residence in host country',
    travelPurpose: 'Purpose of travel',
    entry: 'Entry Information',
    pointOfEntry: 'Point of entry', entryCountry: 'Entry country', arrivalDate: 'Arrival date',
    arrivalTime: 'Arrival time (optional)', transportNumber: 'Flight / vehicle number (optional)',
    originCountry: 'Country of origin', countriesVisited: 'Countries visited in last 21 days',
    health: 'Risk Assessment',
    symptoms: 'Current symptoms — select all that apply', none: 'No symptoms',
    fever: 'Fever / Chills', cough: 'Cough', breathing: 'Difficulty breathing', vomiting: 'Vomiting / Nausea',
    diarrhea: 'Diarrhoea', jaundice: 'Jaundice', rash: 'Skin rash / Lesions', bleeding: 'Unexplained bleeding',
    otherSymptom: 'Other symptom (specify)',
    contactSick: 'Have you cared for or had close physical contact with a sick person in the last 21 days?',
    epidemicArea: 'Have you visited an area with active Ebola / epidemic transmission in the last 21 days?',
    epidemicDetails: 'Please specify the epidemic and location',
    yellowFever: 'Yellow fever vaccination (optional)', yellowFeverDate: 'Vaccination date',
    otherVaccinations: 'Other vaccinations (optional)',
    yes: 'Yes', no: 'No',
    declaration: 'I certify that the information provided is accurate and complete.',
    submit: 'Submit and get health pass', submitting: 'Submitting…',
    qrTitle: 'Screening Complete', qrRef: 'Reference ID',
    qrInstruction: 'Please show this QR code to the health officer at the screening station.',
    required: 'This field is required.',
    downloadPass: 'Download Pass', downloadPassWaiting: 'Pass unavailable — waiting for temperature check',
    passReadyMsg: 'Your pass is ready to download!',
    fixHighlighted: 'Please fill in the fields highlighted in red below.',
    declarationRequired: 'You must confirm this before submitting.',
    newScreening: 'New Screening',
    successSubtitle: 'Your screening has been recorded. Keep this QR code ready for the health officer.',
    stepPhone: 'Identity', stepForm: 'Risk', stepComplete: 'Pass',
    searchCountry: 'Search country or code…',
    secScan: 'Passport / ID Scan',
    haveQr: 'Already have a Voyage Santé QR code?',
    haveQrHint: 'Scan or upload it — your personal details are filled in and you only answer the trip questions.',
    scanQr: 'Scan my QR', uploadQr: 'Upload QR image', qrReading: 'Reading QR code…',
    qrFresh: 'Your last screening is still waiting for the temperature check. Show this QR code to the health officer.',
    qrExpired: 'This QR code is more than 21 days old. Please complete a new screening below.',
    returningTitle: 'Returning traveller — new trip', notYou: 'Not you? Start a new screening',
    prevTrips: 'Previous trips on this QR', tripDate: 'Date', tripRoute: 'From → To', tripTemp: 'Temp.',
    downloadQr: 'Download QR code',
    secIdentity: 'Demographics',
    secHealth: 'Risk Assessment',
    tempNote: 'Body temperature will be measured by a health officer at the checkpoint. A reading ≥ 38 °C will trigger a full assessment.',
    riskNote: 'DRC maintains active Ebola and Mpox surveillance. Answer accurately — false declarations carry legal responsibility.',
    highRisk: 'High risk case',
    footer: 'Your data is processed in accordance with Congolese law n°20/017 on personal data protection.',
    govLabel: 'Ministry of Health',
    govName: 'Democratic Republic of the Congo',
    brandSub: 'Democratic Republic of the Congo',
  },
  fr: {
    title: "Dépistage Santé aux Points d'Entrée",
    subtitle: 'Formulaire Ebola / Mpox — sans connexion requise. Température prise au poste.',
    eyebrow: 'RDC · Ministère de la Santé Publique',
    step1Title: 'Numéro de téléphone & scan du document',
    phone: 'Numéro de téléphone',
    dialCode: 'Indicatif',
    step2Title: "Scannez votre passeport / pièce d'identité (obligatoire)",
    scanHint: 'Prenez une photo ou téléchargez votre passeport — les champs seront pré-remplis.',
    takePhoto: 'Prendre photo',
    upload: 'Télécharger fichier',
    ocrRunning: 'Lecture du document…',
    ocrDone: 'Données pré-remplies depuis votre document — vous pouvez modifier n\'importe quel champ.',
    uploadRequiredNotice: 'Entrez votre numéro de téléphone et scannez ou téléchargez votre passeport / pièce d\'identité pour déverrouiller le formulaire.',
    unlockHint: 'Complétez le numéro de téléphone et le scan du document ci-dessus pour continuer.',
    identity: 'Identité du voyageur',
    fullName: 'Nom complet', sex: 'Genre', male: 'Masculin', female: 'Féminin',
    dob: 'Date de naissance', age: 'Âge (années)', ageHint: 'Calculé automatiquement à partir de la date de naissance.',
    nationality: 'Nationalité', idType: 'Type de pièce', passport: 'Passeport', nationalId: 'Carte nationale', other: 'Autre',
    idNumber: "N° Passeport / Pièce d'ID", residence: 'Résidence dans le pays hôte',
    travelPurpose: 'Motif du voyage',
    entry: "Informations d'entrée",
    pointOfEntry: "Point d'entrée", entryCountry: "Pays d'entrée", arrivalDate: "Date d'arrivée",
    arrivalTime: "Heure d'arrivée (facultatif)", transportNumber: 'Numéro de vol / véhicule (facultatif)',
    originCountry: "Pays d'origine", countriesVisited: 'Pays visités au cours des 21 derniers jours',
    health: 'Évaluation du Risque',
    symptoms: "Symptômes actuels — sélectionnez tout ce qui s'applique", none: 'Aucun symptôme',
    fever: 'Fièvre / Frissons', cough: 'Toux', breathing: 'Difficulté à respirer', vomiting: 'Vomissements / Nausées',
    diarrhea: 'Diarrhée', jaundice: 'Jaunisse', rash: 'Éruption cutanée', bleeding: 'Saignements inexpliqués',
    otherSymptom: 'Autre symptôme (précisez)',
    contactSick: 'Avez-vous soigné ou eu un contact physique rapproché avec un malade dans les 21 jours précédents ?',
    epidemicArea: "Avez-vous visité une zone à transmission active d'Ebola / épidémie dans les 21 jours précédents ?",
    epidemicDetails: "Veuillez préciser l'épidémie et le lieu",
    yellowFever: 'Vaccination contre la fièvre jaune (facultatif)', yellowFeverDate: 'Date de vaccination',
    otherVaccinations: 'Autres vaccinations (facultatif)',
    yes: 'Oui', no: 'Non',
    declaration: 'Je certifie que les informations fournies sont exactes et complètes.',
    submit: 'Soumettre et obtenir le laissez-passer', submitting: 'Envoi en cours…',
    qrTitle: 'Dépistage terminé', qrRef: 'Numéro de référence',
    qrInstruction: "Veuillez présenter ce code QR à l'agent de santé au poste de dépistage.",
    required: 'Ce champ est obligatoire.',
    downloadPass: 'Télécharger le pass', downloadPassWaiting: 'Pass indisponible — en attente du contrôle de température',
    passReadyMsg: 'Votre pass est prêt à être téléchargé !',
    fixHighlighted: 'Veuillez remplir les champs surlignés en rouge ci-dessous.',
    declarationRequired: 'Vous devez confirmer ceci avant de soumettre.',
    newScreening: 'Nouveau dépistage',
    successSubtitle: 'Votre dépistage a été enregistré. Gardez ce code QR prêt pour l\'agent de santé.',
    stepPhone: 'Identité', stepForm: 'Risque', stepComplete: 'Laissez-passer',
    searchCountry: 'Rechercher un pays ou un indicatif…',
    secScan: "Scan Passeport / Pièce d'Identité",
    haveQr: 'Vous avez déjà un code QR Voyage Santé ?',
    haveQrHint: 'Scannez-le ou téléchargez-le — vos informations personnelles sont reprises et vous ne répondez qu\'aux questions du voyage.',
    scanQr: 'Scanner mon QR', uploadQr: 'Télécharger l\'image du QR', qrReading: 'Lecture du code QR…',
    qrFresh: 'Votre dernier dépistage attend encore le contrôle de température. Présentez ce code QR à l\'agent de santé.',
    qrExpired: 'Ce code QR date de plus de 21 jours. Veuillez faire un nouveau dépistage ci-dessous.',
    returningTitle: 'Voyageur connu — nouveau voyage', notYou: 'Ce n\'est pas vous ? Nouveau dépistage',
    prevTrips: 'Voyages précédents sur ce QR', tripDate: 'Date', tripRoute: 'De → À', tripTemp: 'Temp.',
    downloadQr: 'Télécharger le code QR',
    secIdentity: 'Données Démographiques',
    secHealth: 'Évaluation du Risque',
    tempNote: 'La température corporelle sera mesurée par un agent de santé au point de contrôle. Un résultat ≥ 38 °C déclenchera une évaluation approfondie.',
    riskNote: 'La RDC maintient une surveillance active Ebola et Mpox. Répondez avec précision — toute déclaration erronée engagera votre responsabilité.',
    highRisk: 'Cas à haut risque',
    footer: 'Vos données sont traitées conformément à la loi congolaise n°20/017 sur la protection des données personnelles.',
    govLabel: 'Ministère de la Santé',
    govName: 'République Démocratique du Congo',
    brandSub: 'Democratic Republic of the Congo',
  },
};

const SYMPTOM_KEYS = ['fever', 'cough', 'breathing', 'vomiting', 'diarrhea', 'jaundice', 'rash', 'bleeding'] as const;

const DRC = {
  blue: '#007FFF',
  blueDark: '#005BB5',
  blueLight: '#E6F2FF',
  red: '#CE1126',
  yellow: '#F7D618',
  teal: '#0A5C52',
  tealMid: '#1A7A6E',
  tealLight: '#E8F5F3',
  highRisk: '#CE1126',
  highRiskBg: '#FFF0F0',
  bg: '#F7F9FC',
  border: '#E2E8F0',
  textDark: '#1A1A2E',
  textMuted: '#6B7280',
  green: '#289728',
  amberBg: '#FFF8E1',
};

const TOUCH_SX = {
  '& .MuiRadio-root, & .MuiCheckbox-root': { p: 1.1 },
  '& .MuiFormControlLabel-label': { fontSize: '0.95rem' },
};

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px', bgcolor: '#fff',
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: DRC.blue },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: DRC.blue, borderWidth: 1 },
    '&.Mui-focused': { boxShadow: '0 0 0 3px rgba(0,127,255,.1)' },
  },
};

type DialCodeOption = { name: string; iso2: string; dial_code: string };

const FlagBar = () => (
  <Box sx={{ height: 5, display: 'flex', width: '100%' }}>
    <Box sx={{ flex: 1, bgcolor: DRC.blue }} />
    <Box sx={{
      width: 0, height: 0,
      borderTop: '5px solid transparent',
      borderLeft: `28px solid ${DRC.red}`,
    }} />
    <Box sx={{ width: 5, bgcolor: DRC.yellow }} />
  </Box>
);

type CardTone = 'blue' | 'teal' | 'red';

const FormCard: React.FC<{ title: string; tone?: CardTone; badge?: string; children: React.ReactNode }> = ({
  title, tone = 'teal', badge, children,
}) => {
  const tones = {
    blue: { headerBg: DRC.blueLight, iconBg: DRC.blue, title: DRC.blueDark },
    teal: { headerBg: DRC.tealLight, iconBg: DRC.teal, title: DRC.teal },
    red: { headerBg: DRC.highRiskBg, iconBg: DRC.highRisk, title: DRC.highRisk },
  }[tone];
  return (
    <Box sx={{
      bgcolor: '#fff', borderRadius: '14px', border: `1px solid ${DRC.border}`,
      overflow: 'hidden', mb: 1.75,
    }}>
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1.25,
        px: 2.5, py: 1.75, borderBottom: `1px solid ${DRC.border}`, bgcolor: tones.headerBg,
      }}>
        <Box sx={{
          width: 28, height: 28, borderRadius: '50%', bgcolor: tones.iconBg,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#fff' }} />
        </Box>
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: tones.title, flex: 1 }}>{title}</Typography>
        {badge && (
          <Box sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.75,
            bgcolor: DRC.highRiskBg, border: '1px solid #FFCDD2', borderRadius: 20,
            px: 1.25, py: 0.5, fontSize: 11, fontWeight: 600, color: DRC.highRisk,
          }}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: DRC.highRisk }} />
            {badge}
          </Box>
        )}
      </Box>
      <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 1.75 }}>{children}</Box>
    </Box>
  );
};

const StepperBar: React.FC<{ step: 1 | 2 | 3; lang: Lang }> = ({ step, lang }) => {
  const labels = lang === 'fr'
    ? ['Langue', 'Identité', 'Risque', 'Laissez-passer']
    : ['Language', 'Identity', 'Risk', 'Pass'];
  // Map progress: form locked → identity (2), unlocked → risk (3), complete → pass (4)
  const visualStep = step === 1 ? 2 : step === 2 ? 3 : 4;
  return (
    <Box sx={{ display: 'flex', mb: 3, gap: 0 }}>
      {labels.map((label, i) => {
        const n = i + 1;
        const done = visualStep > n;
        const active = visualStep === n;
        return (
          <Box key={label} sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative' }}>
            {i < labels.length - 1 && (
              <Box sx={{
                position: 'absolute', top: 14, left: '62%', width: '76%', height: 2,
                bgcolor: done ? DRC.teal : active ? DRC.blue : DRC.border,
                zIndex: 0,
              }} />
            )}
            <Box sx={{
              width: 28, height: 28, borderRadius: '50%', zIndex: 1, mb: 0.6,
              border: `2px solid ${done ? DRC.teal : active ? DRC.blue : DRC.border}`,
              bgcolor: done ? DRC.teal : active ? DRC.blue : '#fff',
              color: done || active ? '#fff' : DRC.textMuted,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 11, fontWeight: 600,
              boxShadow: active ? '0 0 0 4px rgba(0,127,255,0.15)' : 'none',
            }}>
              {done ? '✓' : n}
            </Box>
            <Typography sx={{
              fontSize: 10, fontWeight: 500, textAlign: 'center', textTransform: 'uppercase',
              letterSpacing: '0.04em', color: active ? DRC.blue : done ? DRC.teal : DRC.textMuted,
            }}>
              {label}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

const ScreeningHeader: React.FC<{
  lang: Lang;
  onLang: (l: Lang) => void;
}> = ({ lang, onLang }) => (
  <Box sx={{ bgcolor: '#fff', borderBottom: `1px solid ${DRC.border}` }}>
    <Box sx={{ height: 3, bgcolor: DRC.blue, maxWidth: 600, mx: 'auto' }} />
    <Box sx={{
      maxWidth: 600, mx: 'auto', px: 2.5, py: 2,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5,
    }}>
      <Box component="img" src="/logo/voyage_sante_logo.png" alt="Voyage Santé+"
        sx={{ height: 48, width: 'auto', maxWidth: 180, objectFit: 'contain' }} />
      <Box component="img" src="/logo/ebola_logo.png" alt="Ministère de la Santé"
        sx={{ height: 48, width: 'auto', maxWidth: 160, objectFit: 'contain', flex: 1 }} />
      <ToggleButtonGroup size="small" value={lang} exclusive
        onChange={(_, v) => v && onLang(v)}
        sx={{
          border: `1px solid ${DRC.border}`, borderRadius: '6px', bgcolor: '#f8f9fa', flexShrink: 0,
          '& .MuiToggleButton-root': { border: 'none', py: 0.5, px: 1.5, fontSize: 12, fontWeight: 600, color: DRC.textMuted },
          '& .Mui-selected': { bgcolor: `${DRC.teal} !important`, color: '#fff !important' },
        }}>
        <ToggleButton value="fr">FR</ToggleButton>
        <ToggleButton value="en">EN</ToggleButton>
      </ToggleButtonGroup>
    </Box>
  </Box>
);

const RequiredMark = () => <Box component="span" sx={{ color: DRC.red, fontSize: 11, ml: 0.25 }}> *</Box>;

const PublicScreeningPage: React.FC = () => {
  const [lang, setLang] = useState<Lang>('fr');
  const t = (k: string) => STR[lang][k] || k;

  const [phase, setPhase] = useState<'form' | 'complete'>('form');
  const [error, setError] = useState<string | null>(null);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const fieldRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const setFieldRef = (key: string) => (el: HTMLDivElement | null) => { fieldRefs.current[key] = el; };

  const [dialCode, setDialCode] = useState('+243');
  const [dialCodes, setDialCodes] = useState<DialCodeOption[]>([]);
  const [phoneLocal, setPhoneLocal] = useState('');
  const [contactId, setContactId] = useState<number | null>(null);
  const [ocrRunning, setOcrRunning] = useState(false);
  const [ocrDone, setOcrDone] = useState(false);
  const [fileUploaded, setFileUploaded] = useState(false);
  // Returning traveller: result of reading their uploaded QR (mode rescreen | fresh | expired)
  const [qrInfo, setQrInfo] = useState<any>(null);
  const [qrBusy, setQrBusy] = useState(false);
  const [qrError, setQrError] = useState<string | null>(null);

  const [entryCities, setEntryCities] = useState<{ id: string; title: string }[]>([]);
  // Entry types that currently have at least one active point of entry (null = not loaded yet)
  const [availableEntryTypes, setAvailableEntryTypes] = useState<string[] | null>(null);
  // country_master rows; origin / visited countries are stored as iso3 (same codes as WhatsApp + agent app)
  const [countries, setCountries] = useState<{ value: string; label: string; iso3: string }[]>([]);

  const [fullName, setFullName] = useState('');
  const [sex, setSex] = useState('');
  const [dob, setDob] = useState('');
  const [age, setAge] = useState('');
  const [nationality, setNationality] = useState('');
  const [idType, setIdType] = useState('passport');
  const [idNumber, setIdNumber] = useState('');
  const [residence, setResidence] = useState('');
  const [travelPurpose, setTravelPurpose] = useState('');

  const [entryType, setEntryType] = useState('Air-Port');
  const [entryCity, setEntryCity] = useState('');
  const [entryCountry] = useState('Democratic Republic of the Congo');
  const [arrivalDate, setArrivalDate] = useState(new Date().toISOString().slice(0, 10));
  const [arrivalTime, setArrivalTime] = useState('');
  const [transportNumber, setTransportNumber] = useState('');
  const [originCountry, setOriginCountry] = useState('');
  const [countriesVisited, setCountriesVisited] = useState<string[]>([]);

  const [symptoms, setSymptoms] = useState<Record<string, boolean>>({});
  const [symptomOther, setSymptomOther] = useState('');
  const [contactSick, setContactSick] = useState<'yes' | 'no' | ''>('');
  const [epidemicArea, setEpidemicArea] = useState<'yes' | 'no' | ''>('');
  const [epidemicDetails, setEpidemicDetails] = useState('');
  const [yellowFever, setYellowFever] = useState<'yes' | 'no' | ''>('');
  const [yellowFeverDate, setYellowFeverDate] = useState('');
  const [otherVaccinations, setOtherVaccinations] = useState('');

  const [declaration, setDeclaration] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [passReady, setPassReady] = useState(false);
  const [showReadySnackbar, setShowReadySnackbar] = useState(false);

  const isRescreen = qrInfo?.mode === 'rescreen';
  const formUnlocked = (phoneLocal.trim().length >= 6 && fileUploaded) || isRescreen;
  const progressStep: 1 | 2 | 3 = phase === 'complete' ? 3 : formUnlocked ? 2 : 1;

  useEffect(() => {
    publicScreeningAPI.dialCodes().then((d) => {
      const sorted = [...(d.dial_codes || [])].sort((a: DialCodeOption, b: DialCodeOption) =>
        a.name.localeCompare(b.name, lang === 'fr' ? 'fr' : 'en')
      );
      setDialCodes(sorted);
    }).catch(() => {});
  }, [lang]);

  const selectedDialCode = dialCodes.find((d) => d.dial_code === dialCode) || null;

  const resetScreening = () => {
    setPhase('form');
    setError(null);
    setSubmitAttempted(false);
    setDialCode('+243');
    setPhoneLocal('');
    setContactId(null);
    setOcrRunning(false);
    setOcrDone(false);
    setFileUploaded(false);
    setFullName('');
    setSex('');
    setDob('');
    setAge('');
    setNationality('');
    setIdType('passport');
    setIdNumber('');
    setResidence('');
    setTravelPurpose('');
    setEntryType('Air-Port');
    setEntryCity('');
    setArrivalDate(new Date().toISOString().slice(0, 10));
    setArrivalTime('');
    setTransportNumber('');
    setOriginCountry('');
    setCountriesVisited([]);
    setSymptoms({});
    setSymptomOther('');
    setContactSick('');
    setEpidemicArea('');
    setEpidemicDetails('');
    setYellowFever('');
    setYellowFeverDate('');
    setOtherVaccinations('');
    setDeclaration(false);
    setSubmitting(false);
    setResult(null);
    setPassReady(false);
    setShowReadySnackbar(false);
    setQrInfo(null);
    setQrBusy(false);
    setQrError(null);
    fieldRefs.current = {};
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    if (phase !== 'complete' || !result?.screening_id || passReady) return;
    const check = () => {
      publicScreeningAPI.status(result.screening_id).then((d) => {
        if (d.ready) {
          setPassReady(true);
          setShowReadySnackbar(true);
        }
      }).catch(() => {});
    };
    check();
    const interval = setInterval(check, 15000);
    return () => clearInterval(interval);
  }, [phase, result, passReady]);

  useEffect(() => {
    if (!formUnlocked) return;
    publicScreeningAPI.entryOptions(entryType).then((d) => setEntryCities(d.entry_cities || [])).catch(() => {});
  }, [formUnlocked, entryType]);

  useEffect(() => {
    if (!formUnlocked) return;
    const types = ['Air-Port', 'Sea-Port', 'Land-Port', 'Railway-Port'];
    Promise.all(types.map((ty) => publicScreeningAPI.entryOptions(ty)
      .then((d) => ((d.entry_cities || []).length ? ty : null))
      .catch(() => ty)))
      .then((res) => {
        const avail = res.filter(Boolean) as string[];
        setAvailableEntryTypes(avail);
        if (avail.length && !avail.includes(entryType)) { setEntryType(avail[0]); setEntryCity(''); }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formUnlocked]);

  useEffect(() => {
    if (!formUnlocked) return;
    publicScreeningAPI.countries(lang).then((d) => setCountries(d.countries || [])).catch(() => {});
  }, [formUnlocked, lang]);

  const handleOcrFile = async (file: File) => {
    setError(null);
    const phone = `${dialCode}${phoneLocal}`.replace(/\s+/g, '');
    if (!phoneLocal.trim()) { setError(t('required')); return; }
    setOcrRunning(true);
    try {
      let cid = contactId;
      if (!cid) {
        const startRes = await publicScreeningAPI.start(phone);
        cid = startRes.contact_id;
        setContactId(cid);
      }
      try {
        const res = await publicScreeningAPI.uploadPassport(file, cid!);
        if (res.full_name && res.full_name !== '—') setFullName(res.full_name);
        if (res.sex) setSex(res.sex === 'M' ? 'Male' : res.sex === 'F' ? 'Female' : res.sex);
        if (res.date_of_birth && res.date_of_birth !== '—') setDob(res.date_of_birth);
        if (res.nationality && res.nationality !== '—') setNationality(res.nationality);
        if (res.doc_number && res.doc_number !== '—') setIdNumber(res.doc_number);
        // Re-screening within 21 days: reuse the personal details from the previous screening
        const prev = res.previous_details;
        if (prev) {
          if (prev.full_name) setFullName(prev.full_name);
          if (prev.sex) setSex(prev.sex === 'M' ? 'Male' : prev.sex === 'F' ? 'Female' : prev.sex);
          if (prev.date_of_birth) setDob(String(prev.date_of_birth).slice(0, 10));
          if (prev.age != null) setAge(String(prev.age));
          if (prev.nationality) setNationality(prev.nationality);
          if (prev.id_type) setIdType(prev.id_type);
          if (prev.id_number) setIdNumber(prev.id_number);
          if (prev.residence_host_country) setResidence(prev.residence_host_country);
        }
        setOcrDone(true);
      } catch {
        setError('Could not read the document — you can still fill the form manually.');
      }
      setFileUploaded(true);
    } catch {
      setError('Could not start screening. Please try again.');
    } finally {
      setOcrRunning(false);
    }
  };

  useEffect(() => {
    if (!dob) return;
    const birth = new Date(dob);
    if (isNaN(birth.getTime())) return;
    const today = new Date();
    let years = today.getFullYear() - birth.getFullYear();
    const hasHadBirthdayThisYear =
      today.getMonth() > birth.getMonth() ||
      (today.getMonth() === birth.getMonth() && today.getDate() >= birth.getDate());
    if (!hasHadBirthdayThisYear) years -= 1;
    if (years >= 0 && years <= 120) setAge(String(years));
  }, [dob]);

  const handleQrFile = async (file: File) => {
    setQrBusy(true);
    setQrError(null);
    setQrInfo(null);
    try {
      const info = await publicScreeningAPI.resolveQr(file);
      setQrInfo(info);
    } catch (e: any) {
      setQrError(e?.response?.data?.detail || 'Could not read this QR code.');
    } finally {
      setQrBusy(false);
    }
  };

  const toggleSymptom = (key: string) => {
    setSymptoms((prev): Record<string, boolean> => {
      if (key === 'none') return prev.none ? {} : { none: true };
      return { ...prev, [key]: !prev[key], none: false };
    });
  };

  const fieldErrors = {
    fullName: !isRescreen && !fullName.trim(),
    sex: !isRescreen && !sex,
    dob: !isRescreen && !dob,
    age: !isRescreen && !age,
    nationality: !isRescreen && !nationality.trim(),
    idNumber: !isRescreen && !idNumber.trim(),
    residence: !isRescreen && !residence.trim(),
    travelPurpose: !travelPurpose,
    entryCity: !entryCity,
    originCountry: !originCountry.trim(),
    symptoms: !SYMPTOM_KEYS.some((k) => symptoms[k]) && !symptoms.none,
    contactSick: !contactSick,
    epidemicArea: !epidemicArea,
    declaration: !declaration,
  };
  const FIELD_ORDER = [
    'fullName', 'sex', 'dob', 'age', 'nationality', 'idNumber', 'residence', 'travelPurpose',
    'entryCity', 'originCountry', 'symptoms', 'contactSick', 'epidemicArea', 'declaration',
  ] as const;
  const showErr = (key: keyof typeof fieldErrors) => submitAttempted && fieldErrors[key];

  const handleSubmit = async () => {
    setError(null);
    setSubmitAttempted(true);
    const firstBad = FIELD_ORDER.find((k) => fieldErrors[k]);
    if (firstBad) {
      setError(t('fixHighlighted'));
      fieldRefs.current[firstBad]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        contact_id: contactId,
        phone_number: `${dialCode}${phoneLocal}`.replace(/\s+/g, ''),
        full_name: fullName || null,
        sex: sex || null,
        date_of_birth: dob || null,
        age: age ? Number(age) : null,
        nationality: nationality || null,
        id_type: idType || null,
        id_number: idNumber || null,
        residence_host_country: residence || null,
        travel_purpose: travelPurpose || null,
        entry_type: entryType || null,
        entry_city: entryCity || null,
        entry_country: entryCountry || null,
        arrival_date: arrivalDate || null,
        arrival_time: arrivalTime || null,
        transport_number: transportNumber || null,
        country_of_origin: originCountry || null,
        countries_visited_21d: countriesVisited,
        symptom_fever: !!symptoms.fever,
        symptom_cough: !!symptoms.cough,
        symptom_breathing: !!symptoms.breathing,
        symptom_vomiting: !!symptoms.vomiting,
        symptom_diarrhea: !!symptoms.diarrhea,
        symptom_jaundice: !!symptoms.jaundice,
        symptom_rash: !!symptoms.rash,
        symptom_bleeding: !!symptoms.bleeding,
        symptom_other: symptomOther || null,
        contact_with_sick: contactSick === 'yes',
        visited_epidemic_area: epidemicArea === 'yes',
        epidemic_details: epidemicArea === 'yes' ? epidemicDetails : null,
        yellow_fever_vaccinated: yellowFever === 'yes',
        yellow_fever_date: yellowFever === 'yes' ? yellowFeverDate || null : null,
        other_vaccinations: otherVaccinations || null,
      };
      let res;
      if (isRescreen) {
        // Personal details come from the record on file — send the trip only
        const trip: Record<string, any> = { ...payload, qr_payload: qrInfo.qr_payload };
        ['contact_id', 'phone_number', 'full_name', 'sex', 'date_of_birth', 'age', 'nationality',
          'id_type', 'id_number', 'residence_host_country'].forEach((k) => delete trip[k]);
        res = await publicScreeningAPI.rescreen(trip);
      } else {
        res = await publicScreeningAPI.submit(payload);
      }
      setResult(res);
      setPhase('complete');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Submission failed — please check the form and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const travelPurposeField = (
    <Box ref={setFieldRef('travelPurpose')}>
      <TextField fullWidth label={<>{t('travelPurpose')}<RequiredMark /></>} value={travelPurpose} onChange={(e) => setTravelPurpose(e.target.value)} select
        error={showErr('travelPurpose')} helperText={showErr('travelPurpose') ? t('required') : ' '}
        sx={fieldSx}>
        {['Tourism', 'Business', 'Transit', 'Health', 'Official', 'Other'].map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
      </TextField>
    </Box>
  );

  const qrDownloadButton = (b64: string, name: string) => (
    <Button
      fullWidth size="large" variant="outlined" component="a"
      href={`data:image/png;base64,${b64}`} download={name}
      sx={{ mt: 2, py: 1.3, borderRadius: 2, fontWeight: 700, borderColor: DRC.teal, color: DRC.teal }}
    >
      {t('downloadQr')}
    </Button>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: DRC.bg, fontFamily: 'Inter, system-ui, sans-serif' }}>
      <FlagBar />
      <ScreeningHeader lang={lang} onLang={setLang} />

      <Box sx={{ maxWidth: 580, mx: 'auto', px: 2, pt: 3, pb: 5 }}>
        {phase === 'form' && (
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography sx={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: DRC.blueDark, mb: 0.75 }}>
              {t('eyebrow')}
            </Typography>
            <Typography sx={{ fontSize: 22, fontWeight: 600, color: DRC.textDark, mb: 0.5 }}>{t('title')}</Typography>
            <Typography sx={{ fontSize: 13, color: DRC.textMuted }}>{t('subtitle')}</Typography>
          </Box>
        )}

        <StepperBar step={progressStep} lang={lang} />

        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

        {phase === 'form' && (
          <>
            {!isRescreen && (
              <Box sx={{ mb: 2, p: 2, borderRadius: 2.5, border: `1px solid ${DRC.border}`, bgcolor: '#fff' }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', color: DRC.textDark }}>{t('haveQr')}</Typography>
                <Typography variant="body2" sx={{ color: DRC.textMuted, mt: 0.5, mb: 1.5 }}>{t('haveQrHint')}</Typography>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Button component="label" variant="outlined" sx={{ borderRadius: 2, borderColor: DRC.teal, color: DRC.teal, fontWeight: 600 }}>
                    {t('scanQr')}
                    <input hidden type="file" accept="image/*" capture="environment"
                      onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) handleQrFile(f); }} />
                  </Button>
                  <Button component="label" variant="outlined" sx={{ borderRadius: 2, borderColor: DRC.teal, color: DRC.teal, fontWeight: 600 }}>
                    {t('uploadQr')}
                    <input hidden type="file" accept="image/*"
                      onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) handleQrFile(f); }} />
                  </Button>
                  {qrBusy && <><CircularProgress size={20} sx={{ color: DRC.teal }} /><Typography variant="body2">{t('qrReading')}</Typography></>}
                </Box>
                {qrError && <Alert severity="error" sx={{ mt: 1.5, borderRadius: 2 }}>{qrError}</Alert>}
                {qrInfo?.mode === 'expired' && <Alert severity="warning" sx={{ mt: 1.5, borderRadius: 2 }}>{t('qrExpired')}</Alert>}
                {qrInfo?.mode === 'fresh' && (
                  <Box sx={{ mt: 1.5, textAlign: 'center' }}>
                    <Alert severity="info" sx={{ borderRadius: 2, textAlign: 'left' }}>{t('qrFresh')}</Alert>
                    {qrInfo.qr_image_base64 && (
                      <>
                        <img src={`data:image/png;base64,${qrInfo.qr_image_base64}`} alt="QR code"
                          style={{ width: 200, height: 200, display: 'block', margin: '12px auto 0' }} />
                        {qrDownloadButton(qrInfo.qr_image_base64, 'VoyageSante-QR.png')}
                      </>
                    )}
                  </Box>
                )}
              </Box>
            )}

            {isRescreen && (
              <FormCard title={t('returningTitle')} tone="blue">
                {[
                  [t('fullName'), qrInfo.traveller?.full_name],
                  [t('sex'), qrInfo.traveller?.sex],
                  [t('idNumber'), qrInfo.traveller?.id_number],
                  [t('nationality'), qrInfo.traveller?.nationality],
                ].map(([label, value]) => (
                  <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                    <Typography variant="body2" sx={{ color: DRC.textMuted }}>{label}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: DRC.textDark, textAlign: 'right' }}>{value || '—'}</Typography>
                  </Box>
                ))}
                {(qrInfo.trips?.length ?? 0) > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: DRC.textDark, mb: 0.5 }}>
                      {t('prevTrips')} ({qrInfo.trips.length})
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '80px 1fr 56px', columnGap: 1, rowGap: 0.5 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: DRC.textMuted }}>{t('tripDate')}</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: DRC.textMuted }}>{t('tripRoute')}</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: DRC.textMuted, textAlign: 'right' }}>{t('tripTemp')}</Typography>
                      {qrInfo.trips.map((trip: any, i: number) => (
                        <React.Fragment key={i}>
                          <Typography variant="body2">{trip.date ? new Date(trip.date).toLocaleDateString('fr-FR') : '—'}</Typography>
                          <Typography variant="body2">{`${trip.from || '—'} → ${trip.to || '—'}`}</Typography>
                          <Typography variant="body2" sx={{ textAlign: 'right' }}>{trip.temperature != null ? `${trip.temperature.toFixed(1)}°` : '—'}</Typography>
                        </React.Fragment>
                      ))}
                    </Box>
                  </Box>
                )}
                {travelPurposeField}
                <Button size="small" onClick={resetScreening} sx={{ alignSelf: 'flex-start', color: DRC.textMuted }}>{t('notYou')}</Button>
              </FormCard>
            )}

            {!isRescreen && (
            <FormCard title={t('secScan')} tone="blue">
              <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', color: DRC.textDark }}>{t('step1Title')}</Typography>
              <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
                <Autocomplete
                  sx={{ width: { xs: '100%', sm: 280 }, flexShrink: 0 }}
                  options={dialCodes}
                  value={selectedDialCode}
                  onChange={(_, v) => setDialCode(v?.dial_code || '+243')}
                  getOptionLabel={(o) => `${o.dial_code} — ${o.name}`}
                  isOptionEqualToValue={(a, b) => a.dial_code === b.dial_code && a.iso2 === b.iso2}
                  filterOptions={(opts, { inputValue }) => {
                    const q = inputValue.trim().toLowerCase();
                    if (!q) return opts;
                    return opts.filter((o) =>
                      o.name.toLowerCase().includes(q) ||
                      o.iso2.toLowerCase().includes(q) ||
                      o.dial_code.replace('+', '').includes(q.replace('+', ''))
                    );
                  }}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} key={`${option.iso2}-${option.dial_code}`}>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                        <Chip label={option.dial_code} size="small" sx={{ fontWeight: 700, minWidth: 56 }} />
                        <Typography variant="body2" sx={{ flex: 1 }}>{option.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{option.iso2}</Typography>
                      </Stack>
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField {...params} label={t('dialCode')} placeholder={t('searchCountry')} sx={fieldSx} />
                  )}
                />
                <TextField
                  fullWidth label={t('phone')} value={phoneLocal}
                  inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                  onChange={(e) => setPhoneLocal(e.target.value.replace(/[^\d]/g, ''))}
                  sx={fieldSx}
                />
              </Box>

              <Typography variant="body2" sx={{ color: DRC.textMuted, mt: 1 }}>{t('scanHint')}</Typography>
              <Box sx={{
                border: `2px dashed ${DRC.border}`, borderRadius: 2.5, p: 2.5, textAlign: 'center',
                bgcolor: '#FAFBFC', transition: 'border-color 0.15s',
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                  <Button component="label" variant="outlined" size="medium"
                    sx={{ borderRadius: 2, borderColor: DRC.blue, color: DRC.blue, fontWeight: 600, '&:hover': { bgcolor: DRC.blueLight } }}>
                    {t('takePhoto')}
                    <input hidden type="file" accept="image/*" capture="environment"
                      onChange={(e) => e.target.files?.[0] && handleOcrFile(e.target.files[0])} />
                  </Button>
                  <Button component="label" variant="outlined" size="medium"
                    sx={{ borderRadius: 2, borderColor: DRC.blue, color: DRC.blue, fontWeight: 600, '&:hover': { bgcolor: DRC.blueLight } }}>
                    {t('upload')}
                    <input hidden type="file" accept="image/*,.pdf"
                      onChange={(e) => e.target.files?.[0] && handleOcrFile(e.target.files[0])} />
                  </Button>
                  {ocrRunning && <CircularProgress size={24} sx={{ alignSelf: 'center', color: DRC.blue }} />}
                </Box>
              </Box>
              {ocrRunning && <Alert severity="info" sx={{ borderRadius: 2 }}>{t('ocrRunning')}</Alert>}
              {ocrDone && !ocrRunning && <Alert severity="success" sx={{ borderRadius: 2 }}>{t('ocrDone')}</Alert>}
              {!formUnlocked && (
                <Alert severity="warning" sx={{ borderRadius: 2, bgcolor: DRC.amberBg, border: '1px solid #FFE082' }}>
                  {phoneLocal.trim() ? t('uploadRequiredNotice') : t('unlockHint')}
                </Alert>
              )}
            </FormCard>
            )}

            {formUnlocked && (
              <>
                {!isRescreen && (
                <FormCard title={t('secIdentity')} tone="teal">
                  <Box ref={setFieldRef('fullName')}>
                    <TextField fullWidth label={<>{t('fullName')}<RequiredMark /></>} value={fullName}
                      onChange={(e) => setFullName(e.target.value)} inputProps={{ maxLength: 100 }}
                      error={showErr('fullName')} helperText={showErr('fullName') ? t('required') : ' '}
                      sx={fieldSx} />
                  </Box>
                  <Box ref={setFieldRef('sex')}>
                    <FormControl error={showErr('sex')}>
                      <Typography variant="body2" sx={{ mb: 0.5, color: showErr('sex') ? DRC.red : DRC.textDark }}>
                        {t('sex')}<RequiredMark />
                      </Typography>
                      <RadioGroup row value={sex} onChange={(e) => setSex(e.target.value)} sx={TOUCH_SX}>
                        <FormControlLabel value="Male" control={<Radio sx={{ color: DRC.teal, '&.Mui-checked': { color: DRC.teal } }} />} label={t('male')} />
                        <FormControlLabel value="Female" control={<Radio sx={{ color: DRC.teal, '&.Mui-checked': { color: DRC.teal } }} />} label={t('female')} />
                      </RadioGroup>
                      {showErr('sex') && <FormHelperText>{t('required')}</FormHelperText>}
                    </FormControl>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Box ref={setFieldRef('dob')} sx={{ flex: 1, minWidth: 150 }}>
                      <TextField fullWidth label={<>{t('dob')}<RequiredMark /></>} type="date" value={dob} onChange={(e) => setDob(e.target.value)}
                        InputLabelProps={{ shrink: true }} inputProps={{ max: new Date().toISOString().slice(0, 10) }}
                        error={showErr('dob')} helperText={showErr('dob') ? t('required') : ' '}
                        sx={fieldSx} />
                    </Box>
                    <Box ref={setFieldRef('age')} sx={{ flex: 1, minWidth: 130 }}>
                      <TextField fullWidth label={<>{t('age')}<RequiredMark /></>} type="number" value={age} onChange={(e) => setAge(e.target.value)}
                        inputProps={{ min: 0, max: 120 }} error={showErr('age')}
                        helperText={showErr('age') ? t('required') : t('ageHint')}
                        sx={fieldSx} />
                    </Box>
                  </Box>
                  <Box ref={setFieldRef('nationality')}>
                    <Autocomplete
                      options={countries.map((c) => c.label)}
                      value={nationality}
                      onChange={(_, v) => setNationality(v || '')}
                      renderInput={(params) => (
                        <TextField {...params} label={<>{t('nationality')}<RequiredMark /></>}
                          error={showErr('nationality')} helperText={showErr('nationality') ? t('required') : ' '}
                          sx={fieldSx} />
                      )}
                      freeSolo
                    />
                  </Box>
                  <Box>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>{t('idType')}</Typography>
                    <RadioGroup row value={idType} onChange={(e) => setIdType(e.target.value)} sx={TOUCH_SX}>
                      <FormControlLabel value="passport" control={<Radio sx={{ color: DRC.teal, '&.Mui-checked': { color: DRC.teal } }} />} label={t('passport')} />
                      <FormControlLabel value="national_id" control={<Radio sx={{ color: DRC.teal, '&.Mui-checked': { color: DRC.teal } }} />} label={t('nationalId')} />
                      <FormControlLabel value="other" control={<Radio sx={{ color: DRC.teal, '&.Mui-checked': { color: DRC.teal } }} />} label={t('other')} />
                    </RadioGroup>
                  </Box>
                  <Box ref={setFieldRef('idNumber')}>
                    <TextField fullWidth label={<>{t('idNumber')}<RequiredMark /></>} value={idNumber} onChange={(e) => setIdNumber(e.target.value)}
                      inputProps={{ maxLength: 30 }} error={showErr('idNumber')} helperText={showErr('idNumber') ? t('required') : ' '}
                      sx={fieldSx} />
                  </Box>
                  <Box ref={setFieldRef('residence')}>
                    <TextField fullWidth label={<>{t('residence')}<RequiredMark /></>} value={residence}
                      onChange={(e) => setResidence(e.target.value)}
                      error={showErr('residence')} helperText={showErr('residence') ? t('required') : ' '}
                      sx={fieldSx} />
                  </Box>
                  {travelPurposeField}
                </FormCard>
                )}

                <FormCard title={t('entry')} tone="teal">
                  <RadioGroup row value={entryType} onChange={(e) => { setEntryType(e.target.value); setEntryCity(''); }} sx={TOUCH_SX}>
                    {(!availableEntryTypes || availableEntryTypes.includes('Air-Port')) && <FormControlLabel value="Air-Port" control={<Radio sx={{ color: DRC.teal, '&.Mui-checked': { color: DRC.teal } }} />} label="Airport" />}
                    {(!availableEntryTypes || availableEntryTypes.includes('Sea-Port')) && <FormControlLabel value="Sea-Port" control={<Radio sx={{ color: DRC.teal, '&.Mui-checked': { color: DRC.teal } }} />} label="Port" />}
                    {(!availableEntryTypes || availableEntryTypes.includes('Land-Port')) && <FormControlLabel value="Land-Port" control={<Radio sx={{ color: DRC.teal, '&.Mui-checked': { color: DRC.teal } }} />} label="Land border" />}
                    {(!availableEntryTypes || availableEntryTypes.includes('Railway-Port')) && <FormControlLabel value="Railway-Port" control={<Radio sx={{ color: DRC.teal, '&.Mui-checked': { color: DRC.teal } }} />} label="Railway" />}
                  </RadioGroup>
                  <Box ref={setFieldRef('entryCity')}>
                    <TextField fullWidth label={<>{t('pointOfEntry')}<RequiredMark /></>} value={entryCity} onChange={(e) => setEntryCity(e.target.value)} select
                      error={showErr('entryCity')} helperText={showErr('entryCity') ? t('required') : ' '}
                      sx={fieldSx}>
                      {entryCities.map((c) => <MenuItem key={c.id} value={c.title}>{c.title}</MenuItem>)}
                    </TextField>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <TextField label={t('arrivalDate')} type="date" value={arrivalDate} onChange={(e) => setArrivalDate(e.target.value)}
                      InputLabelProps={{ shrink: true }} inputProps={{ max: new Date().toISOString().slice(0, 10) }}
                      sx={{ flex: 1, minWidth: 150, ...fieldSx }} />
                    <TextField label={t('arrivalTime')} type="time" value={arrivalTime} onChange={(e) => setArrivalTime(e.target.value)}
                      InputLabelProps={{ shrink: true }} sx={{ flex: 1, minWidth: 130, ...fieldSx }} />
                  </Box>
                  <TextField fullWidth label={t('transportNumber')} value={transportNumber} onChange={(e) => setTransportNumber(e.target.value)}
                    inputProps={{ maxLength: 20 }} sx={fieldSx} />
                  <Box ref={setFieldRef('originCountry')}>
                    <Autocomplete
                      options={countries}
                      getOptionLabel={(c) => c.label}
                      isOptionEqualToValue={(a, b) => a.iso3 === b.iso3}
                      value={countries.find((c) => c.iso3 === originCountry) || null}
                      onChange={(_, c) => setOriginCountry(c?.iso3 || '')}
                      renderInput={(params) => (
                        <TextField {...params} label={<>{t('originCountry')}<RequiredMark /></>}
                          error={showErr('originCountry')} helperText={showErr('originCountry') ? t('required') : ' '}
                          sx={fieldSx} />
                      )}
                    />
                  </Box>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>{t('countriesVisited')}</Typography>
                  <Box sx={{ maxHeight: 200, overflowY: 'auto', border: `1px solid ${DRC.border}`, borderRadius: 2, p: 1, bgcolor: '#fff' }}>
                    <FormGroup sx={TOUCH_SX}>
                      {countries.map((c) => (
                        <FormControlLabel
                          key={c.value}
                          control={
                            <Checkbox
                              checked={countriesVisited.includes(c.iso3)}
                              onChange={() =>
                                setCountriesVisited((prev) =>
                                  prev.includes(c.iso3) ? prev.filter((x) => x !== c.iso3) : [...prev, c.iso3]
                                )
                              }
                              sx={{ color: DRC.teal, '&.Mui-checked': { color: DRC.teal } }}
                            />
                          }
                          label={c.label}
                        />
                      ))}
                    </FormGroup>
                  </Box>
                </FormCard>

                <FormCard title={t('secHealth')} tone="red" badge={t('highRisk')}>
                  <Box sx={{
                    display: 'flex', alignItems: 'flex-start', gap: 1, p: 1.5,
                    bgcolor: DRC.highRiskBg, borderRadius: 2, border: '1px solid #FFCDD2',
                    fontSize: 12, color: '#8B0000', fontWeight: 500,
                  }}>
                    {t('riskNote')}
                  </Box>
                  <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 1.25, p: 1.5,
                    bgcolor: DRC.amberBg, borderRadius: 2, border: '1px solid #FFE082',
                    fontSize: 12, color: '#7A5800', fontWeight: 500,
                  }}>
                    {t('tempNote')}
                  </Box>

                  <Box ref={setFieldRef('symptoms')}>
                    <Typography variant="body2" sx={{ mb: 1, color: showErr('symptoms') ? DRC.red : DRC.textDark }}>
                      {t('symptoms')}<RequiredMark />
                    </Typography>
                    <FormGroup sx={TOUCH_SX}>
                      {SYMPTOM_KEYS.map((k) => (
                        <FormControlLabel key={k} control={
                          <Checkbox checked={!!symptoms[k]} onChange={() => toggleSymptom(k)}
                            sx={{ color: DRC.teal, '&.Mui-checked': { color: DRC.teal } }} />
                        } label={t(k)} />
                      ))}
                      <FormControlLabel control={
                        <Checkbox checked={!!symptoms.none} onChange={() => toggleSymptom('none')}
                          sx={{ color: DRC.teal, '&.Mui-checked': { color: DRC.teal } }} />
                      } label={t('none')} />
                    </FormGroup>
                    {showErr('symptoms') && <FormHelperText error>{t('required')}</FormHelperText>}
                  </Box>
                  <TextField fullWidth label={t('otherSymptom')} value={symptomOther}
                    onChange={(e) => setSymptomOther(e.target.value.slice(0, 200))} disabled={!!symptoms.none} sx={fieldSx} />

                  <Box ref={setFieldRef('contactSick')}>
                    <Typography variant="body2" sx={{ mb: 0.5, color: showErr('contactSick') ? DRC.red : DRC.textDark }}>
                      {t('contactSick')}<RequiredMark />
                    </Typography>
                    <RadioGroup row value={contactSick} onChange={(e) => setContactSick(e.target.value as 'yes' | 'no')} sx={TOUCH_SX}>
                      <FormControlLabel value="yes" control={<Radio sx={{ color: DRC.teal, '&.Mui-checked': { color: DRC.teal } }} />} label={t('yes')} />
                      <FormControlLabel value="no" control={<Radio sx={{ color: DRC.teal, '&.Mui-checked': { color: DRC.teal } }} />} label={t('no')} />
                    </RadioGroup>
                    {showErr('contactSick') && <FormHelperText error>{t('required')}</FormHelperText>}
                  </Box>

                  <Box ref={setFieldRef('epidemicArea')}>
                    <Typography variant="body2" sx={{ mb: 0.5, color: showErr('epidemicArea') ? DRC.red : DRC.textDark }}>
                      {t('epidemicArea')}<RequiredMark />
                    </Typography>
                    <RadioGroup row value={epidemicArea} onChange={(e) => setEpidemicArea(e.target.value as 'yes' | 'no')} sx={TOUCH_SX}>
                      <FormControlLabel value="yes" control={<Radio sx={{ color: DRC.teal, '&.Mui-checked': { color: DRC.teal } }} />} label={t('yes')} />
                      <FormControlLabel value="no" control={<Radio sx={{ color: DRC.teal, '&.Mui-checked': { color: DRC.teal } }} />} label={t('no')} />
                    </RadioGroup>
                    {showErr('epidemicArea') && <FormHelperText error>{t('required')}</FormHelperText>}
                    {epidemicArea === 'yes' && (
                      <TextField fullWidth sx={{ mt: 1, ...fieldSx }} label={t('epidemicDetails')} value={epidemicDetails} onChange={(e) => setEpidemicDetails(e.target.value)} />
                    )}
                  </Box>

                  <Box>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>{t('yellowFever')}</Typography>
                    <RadioGroup row value={yellowFever} onChange={(e) => setYellowFever(e.target.value as 'yes' | 'no')} sx={TOUCH_SX}>
                      <FormControlLabel value="yes" control={<Radio sx={{ color: DRC.teal, '&.Mui-checked': { color: DRC.teal } }} />} label={t('yes')} />
                      <FormControlLabel value="no" control={<Radio sx={{ color: DRC.teal, '&.Mui-checked': { color: DRC.teal } }} />} label={t('no')} />
                    </RadioGroup>
                    {yellowFever === 'yes' && (
                      <TextField sx={{ mt: 1, ...fieldSx }} label={t('yellowFeverDate')} type="date" value={yellowFeverDate}
                        onChange={(e) => setYellowFeverDate(e.target.value)} InputLabelProps={{ shrink: true }} />
                    )}
                  </Box>
                  <TextField fullWidth label={t('otherVaccinations')} value={otherVaccinations}
                    onChange={(e) => setOtherVaccinations(e.target.value.slice(0, 200))} sx={fieldSx} />
                </FormCard>

                <Box ref={setFieldRef('declaration')} sx={{
                  p: 2, mb: 2, borderRadius: 2,
                  bgcolor: showErr('declaration') ? '#fdecea' : '#fafbfc',
                  border: showErr('declaration') ? `1px solid ${DRC.red}` : `1px solid ${DRC.border}`,
                }}>
                  <FormControlLabel
                    control={<Checkbox checked={declaration} onChange={(e) => setDeclaration(e.target.checked)}
                      sx={{ color: DRC.teal, '&.Mui-checked': { color: DRC.teal } }} />}
                    label={t('declaration')}
                  />
                  {showErr('declaration') && <FormHelperText error>{t('declarationRequired')}</FormHelperText>}
                </Box>

                <Button
                  fullWidth size="large"
                  disabled={submitting} onClick={handleSubmit}
                  sx={{
                    height: 50, borderRadius: 2.5, fontWeight: 600, fontSize: 15,
                    bgcolor: DRC.teal, color: '#fff',
                    '&:hover': { bgcolor: DRC.tealMid },
                  }}
                >
                  {submitting ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : t('submit')}
                </Button>
              </>
            )}
          </>
        )}

        {phase === 'complete' && result && (
          <Box sx={{ textAlign: 'center' }}>
            <CheckCircleOutlineIcon sx={{ fontSize: 56, color: DRC.green, mb: 1 }} />
            <Typography sx={{ fontWeight: 800, fontSize: '1.35rem', color: DRC.textDark, mb: 0.5 }}>{t('qrTitle')}</Typography>
            <Typography variant="body2" sx={{ color: DRC.textMuted, mb: 3, px: 1 }}>{t('successSubtitle')}</Typography>

            <Box sx={{
              display: 'inline-block', p: 2.5, bgcolor: '#fff', borderRadius: 3,
              border: `2px solid ${DRC.border}`, boxShadow: '0 8px 24px rgba(10,92,82,0.08)',
            }}>
              <img
                src={`data:image/png;base64,${result.qr_image_base64}`}
                alt="QR code"
                style={{ width: 220, height: 220, display: 'block' }}
              />
            </Box>

            <Chip label={`${t('qrRef')}: ${result.reference_id}`}
              sx={{ mt: 2.5, fontWeight: 700, px: 1, py: 2.2, fontSize: '0.9rem', bgcolor: DRC.tealLight, color: DRC.teal }} />
            <Typography sx={{ mt: 2, fontWeight: 700, fontSize: '1.1rem', color: DRC.textDark }}>{isRescreen ? qrInfo.traveller?.full_name : fullName}</Typography>
            {!isRescreen && <Typography variant="body2" sx={{ color: DRC.textMuted }}>{dialCode}{phoneLocal}</Typography>}
            <Typography variant="body2" sx={{ mt: 2, color: DRC.textMuted, px: 2 }}>{t('qrInstruction')}</Typography>
            {qrDownloadButton(result.qr_image_base64, `VoyageSante-QR-${result.reference_id}.png`)}

            <Button
              fullWidth size="large"
              disabled={!passReady}
              component="a"
              href={passReady ? publicScreeningAPI.passPdfUrl(result.screening_id) : undefined}
              target="_blank" rel="noopener noreferrer"
              sx={{
                mt: 3, py: 1.5, borderRadius: 2, fontWeight: 700,
                bgcolor: DRC.teal, color: '#fff', '&:hover': { bgcolor: DRC.tealMid },
              }}
            >
              {passReady ? t('downloadPass') : <><CircularProgress size={18} sx={{ mr: 1, color: '#fff' }} />{t('downloadPassWaiting')}</>}
            </Button>

            <Button
              fullWidth size="large" startIcon={<AddCircleOutlineIcon />}
              onClick={resetScreening}
              sx={{ mt: 2, py: 1.3, borderRadius: 2, fontWeight: 600, bgcolor: DRC.green, color: '#fff', '&:hover': { bgcolor: '#228B22' } }}
            >
              {t('newScreening')}
            </Button>
          </Box>
        )}

        <Snackbar
          open={showReadySnackbar}
          onClose={(_e, reason) => reason !== 'clickaway' && setShowReadySnackbar(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity="success" onClose={() => setShowReadySnackbar(false)}>{t('passReadyMsg')}</Alert>
        </Snackbar>

        <Typography sx={{
          textAlign: 'center', fontSize: 11, color: DRC.textMuted, mt: 2, px: 2.5, pb: 1, lineHeight: 1.6,
        }}>
          {t('footer')}
        </Typography>
      </Box>
    </Box>
  );
};

export default PublicScreeningPage;
